import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Workflow, Plus, Search, Trash2, Tag, X, Play } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    archived: "bg-white/5 border-white/20 text-white/40",
};

export default function FlowsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ draft: 0, active: 0, archived: 0, total_all: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [createOpen, setCreateOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            const { data } = await api.get(`/admin/flows?${params.toString()}`);
            setItems(data.items || []);
            setStats(data.stats || {});
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const remove = async (id, name) => {
        const reason = window.prompt(`Delete flow "${name}"? Provide a reason for the audit log:`);
        if (!reason || reason.length < 3) return;
        try {
            await api.delete(`/admin/flows/${id}?reason=${encodeURIComponent(reason)}`);
            toast.success("Flow deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="flows-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Workflow className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.6} />
                        <h1 className="font-display text-2xl tracking-tight">Flows</h1>
                    </div>
                    <div className="text-sm text-white/50 mt-1 max-w-xl">
                        Orchestrate AI agents and external APIs. Sequential + parallel + conditional + manual-approval nodes.
                    </div>
                </div>
                <button onClick={() => setCreateOpen(true)} data-testid="flows-create"
                    className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> New flow
                </button>
            </div>

            <div className="grid grid-cols-4 gap-3">
                {[
                    { label: "Draft", v: stats.draft, color: "text-amber-400" },
                    { label: "Active", v: stats.active, color: "text-emerald-400" },
                    { label: "Archived", v: stats.archived, color: "text-white/40" },
                    { label: "Total", v: stats.total_all, color: "text-[#7C3AED]" },
                ].map((c) => (
                    <div key={c.label} className="border border-white/10 bg-[#0a0a0a] rounded-md p-4">
                        <div className="label-xs text-white/40">{c.label}</div>
                        <div className={`mt-1 font-mono text-2xl ${c.color}`}>{c.v ?? 0}</div>
                    </div>
                ))}
            </div>

            <div className="flex items-center gap-2">
                <div className="flex items-center bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 flex-1 max-w-md">
                    <Search className="w-3.5 h-3.5 text-white/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search flows…" data-testid="flows-search"
                        className="bg-transparent ml-2 outline-none text-sm flex-1" />
                </div>
                <select value={status} onChange={(e) => { setStatus(e.target.value); setTimeout(load, 0); }}
                    data-testid="flows-status-filter" className="bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 text-xs">
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                </select>
                <button onClick={load} className="px-3 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5">Apply</button>
            </div>

            <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                        <tr className="text-left label-xs text-white/40">
                            <th className="px-5 py-3">Name</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3">Nodes</th>
                            <th className="px-5 py-3">Version</th>
                            <th className="px-5 py-3">Runs</th>
                            <th className="px-5 py-3">Tags</th>
                            <th className="px-5 py-3">Updated</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="px-5 py-10 text-center text-white/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={8} className="px-5 py-12 text-center text-white/40 text-sm" data-testid="flows-empty">
                                No flows yet. Click <span className="text-[#7C3AED]">New flow</span> to orchestrate your first multi-step workflow.
                            </td></tr>
                        ) : items.map((f) => (
                            <tr key={f.id} onClick={() => navigate(`/flows/${f.id}`)}
                                data-testid={`flow-row-${f.id}`}
                                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer">
                                <td className="px-5 py-3">
                                    <div className="font-medium text-white">{f.name}</div>
                                    <div className="font-mono text-[10px] text-white/40">{f.slug}</div>
                                </td>
                                <td className="px-5 py-3">
                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_BADGE[f.status] || ""}`}>
                                        {f.status}
                                    </span>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{f.nodes?.length || 0}</td>
                                <td className="px-5 py-3 font-mono text-xs">v{f.active_version_no} <span className="text-white/30">/ {f.version_count}</span></td>
                                <td className="px-5 py-3 font-mono text-xs">{f.run_count || 0}</td>
                                <td className="px-5 py-3">
                                    <div className="flex flex-wrap gap-1">
                                        {(f.tags || []).slice(0, 3).map((t) => (
                                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60 flex items-center gap-1">
                                                <Tag className="w-2.5 h-2.5" /> {t}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-5 py-3 font-mono text-[10px] text-white/40">
                                    {f.updated_at ? new Date(f.updated_at).toLocaleString() : "—"}
                                </td>
                                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => remove(f.id, f.name)} data-testid={`flow-delete-${f.id}`}
                                        className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {createOpen && (
                <CreateFlowModal
                    onClose={() => setCreateOpen(false)}
                    onCreated={(f) => { setCreateOpen(false); navigate(`/flows/${f.id}`); }}
                />
            )}
        </div>
    );
}

const TEMPLATE_NODES = [
    {
        id: "n1", name: "Normalize input", type: "transform",
        input_mapping: { text: "{{input.text}}", upper: "{{input.text}}" },
        config: {}, timeout_seconds: 30,
    },
    {
        id: "n2", name: "Final output", type: "transform",
        input_mapping: { result: "{{steps.n1.output.text}}", note: "edit me" },
        config: {}, timeout_seconds: 30,
    },
];

function CreateFlowModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Name required");
        setBusy(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || null,
                tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
                input_schema: { type: "object", properties: { text: { type: "string" } } },
                output_schema: { mapping: { final: "{{steps.n2.output}}" } },
                nodes: TEMPLATE_NODES,
                status: "draft",
            };
            const { data } = await api.post("/admin/flows", body);
            toast.success(`Flow created: ${data.slug}`);
            onCreated(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="create-flow-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <Workflow className="w-4 h-4 text-[#7C3AED]" />
                        <div className="font-display text-lg tracking-tight">New flow</div>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <div className="label-xs mb-1.5">Name *</div>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-flow-name"
                            placeholder="e.g. Protocol ingest → summarize → notify"
                            className="input" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Description</div>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="input" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Tags (comma-separated)</div>
                        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="clinical, ingest" className="input text-xs" />
                    </div>
                    <div className="border border-white/10 bg-[#050505] rounded-md p-3 text-[10px] text-white/50">
                        A template 2-node flow will be created (transform → agent). You'll configure the agent id and prompts in the editor.
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                        <button type="submit" disabled={busy} data-testid="create-flow-submit"
                            className="flex-1 bg-[#7C3AED] text-white py-2 rounded-md text-sm font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                            {busy ? "Creating…" : "Create flow"}
                        </button>
                        <button type="button" onClick={onClose}
                            className="px-4 py-2 border border-white/15 rounded-md text-sm hover:bg-white/5">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
